const GIS_SRC = "https://accounts.google.com/gsi/client";

export interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

export interface GoogleIdApi {
  initialize(config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    nonce?: string;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    use_fedcm_for_prompt?: boolean;
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      type?: "standard" | "icon";
      theme?: "outline" | "filled_blue" | "filled_black";
      size?: "large" | "medium" | "small";
      text?: "signin_with" | "signup_with" | "continue_with" | "signin";
      shape?: "rectangular" | "pill" | "circle" | "square";
      logo_alignment?: "left" | "center";
      width?: number;
      locale?: string;
    }
  ): void;
  disableAutoSelect(): void;
}

interface GoogleAccountsGlobal {
  accounts: { id: GoogleIdApi };
}

declare global {
  interface Window {
    google?: GoogleAccountsGlobal;
  }
}

let gisPromise: Promise<GoogleIdApi> | null = null;

export function loadGoogleIdentityServices(timeoutMs = 8000): Promise<GoogleIdApi> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("Google Identity Services unavailable: no DOM"));
  }

  if (window.google?.accounts?.id) {
    return Promise.resolve(window.google.accounts.id);
  }

  if (gisPromise) return gisPromise;

  gisPromise = new Promise<GoogleIdApi>((resolve, reject) => {
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);

      const api = window.google?.accounts?.id;
      if (error || !api) {
        gisPromise = null;
        reject(error ?? new Error("Google Identity Services loaded without accounts.id"));
        return;
      }

      resolve(api);
    };

    const timer = setTimeout(
      () => finish(new Error("Google Identity Services load timed out")),
      timeoutMs
    );

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    if (existing) {
      if (window.google?.accounts?.id) {
        finish();
        return;
      }
      existing.addEventListener("load", () => finish());
      existing.addEventListener("error", () =>
        finish(new Error("Google Identity Services script failed to load"))
      );
      return;
    }

    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => finish());
    script.addEventListener("error", () =>
      finish(new Error("Google Identity Services script failed to load"))
    );
    document.head.appendChild(script);
  });

  return gisPromise;
}

export async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export interface NoncePair {
  raw: string;
  hashed: string;
}

export async function generateNonce(): Promise<NoncePair> {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const raw = Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
  return { raw, hashed: await sha256Hex(raw) };
}
